import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import cacheManager from '../CacheManager';
import { formatDate, formatCurrency } from '../js/dateFormatter';

const PgDashboard = () => {
  const [clienteData, setClienteData] = useState(null);
  const [proximosAgendamentos, setProximosAgendamentos] = useState([]);
  const [faturasAbertas, setFaturasAbertas] = useState([]);
  const [itensByIdCache, setItensByIdCache] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);

      // Carregar dados do cliente
      const cliente = await cacheManager.getClienteData();
      setClienteData(cliente);

      // Carregar itens para referência
      const itensById = await cacheManager.getItensById();
      setItensByIdCache(itensById);

      // Carregar agendamentos
      const agendamentos = await cacheManager.getAgendamentosCache();
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      
      // Filtrar apenas agendamentos futuros e ordenar
      const futuros = agendamentos
        .filter(ag => new Date(ag.data_inicio_agendamento) >= hoje)
        .sort((a, b) => new Date(a.data_inicio_agendamento) - new Date(b.data_inicio_agendamento))
        .slice(0, 5); // Apenas os próximos 5
      
      setProximosAgendamentos(futuros);

      // Carregar faturas abertas
      const faturas = await cacheManager.getFaturasCache();
      const abertas = faturas
        .filter(f => f.status === 'aberta' || f.status === 'pendente')
        .sort((a, b) => new Date(a.data_vencimento) - new Date(b.data_vencimento));
      
      setFaturasAbertas(abertas);

    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container mt-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Carregando...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      {/* Cabeçalho de Boas-vindas */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card border-0 shadow-sm" style={{ backgroundColor: '#27355c', color: 'white' }}>
            <div className="card-body p-4">
              <h2 className="mb-2">Bem-vindo, {clienteData?.nome?.split(' ')[0] || 'Cliente'}!</h2>
              <p className="mb-0">Aqui você encontra todas as informações sobre seus agendamentos e faturas.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Cards de Resumo */}
      <div className="row mb-4">
        <div className="col-md-4 mb-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body text-center">
              <i className="bi bi-calendar-check fs-1 text-primary mb-3"></i>
              <h5 className="card-title">Próximos Agendamentos</h5>
              <h2 className="text-primary">{proximosAgendamentos.length}</h2>
            </div>
          </div>
        </div>
        <div className="col-md-4 mb-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body text-center">
              <i className="bi bi-receipt fs-1 text-warning mb-3"></i>
              <h5 className="card-title">Faturas em Aberto</h5>
              <h2 className="text-warning">{faturasAbertas.length}</h2>
            </div>
          </div>
        </div>
        <div className="col-md-4 mb-3">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body text-center">
              <i className="bi bi-currency-dollar fs-1 text-danger mb-3"></i>
              <h5 className="card-title">Valor Total em Aberto</h5>
              <h2 className="text-danger">
                {formatCurrency(faturasAbertas.reduce((total, f) => total + parseFloat(f.valor_total || 0), 0))}
              </h2>
            </div>
          </div>
        </div>
      </div>

      {/* Próximos Agendamentos */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-0 d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Próximos Agendamentos</h5>
              <Link to="/meus-agendamentos" className="btn btn-sm btn-outline-primary">
                Ver Todos
              </Link>
            </div>
            <div className="card-body">
              {proximosAgendamentos.length === 0 ? (
                <p className="text-muted text-center py-3">Nenhum agendamento futuro encontrado.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th>Data</th>
                        <th>Horário</th>
                      </tr>
                    </thead>
                    <tbody>
                      {proximosAgendamentos.map((ag, index) => (
                        <tr key={index}>
                          <td>{itensByIdCache[ag.id_item]?.titulo || 'Item não encontrado'}</td>
                          <td>{formatDate(ag.data_inicio_agendamento)}</td>
                          <td>
                            {new Date(ag.data_inicio_agendamento).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                            {' - '}
                            {new Date(ag.data_fim_agendamento).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Faturas em Aberto */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-0 d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Faturas em Aberto</h5>
              <Link to="/minhas-faturas" className="btn btn-sm btn-outline-primary">
                Ver Todas
              </Link>
            </div>
            <div className="card-body">
              {faturasAbertas.length === 0 ? (
                <p className="text-muted text-center py-3">Nenhuma fatura em aberto.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Nº Fatura</th>
                        <th>Vencimento</th>
                        <th>Valor</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {faturasAbertas.map((fatura, index) => (
                        <tr key={index}>
                          <td>#{fatura.id_fatura}</td>
                          <td>{formatDate(fatura.data_vencimento)}</td>
                          <td className="fw-bold">{formatCurrency(fatura.valor_total)}</td>
                          <td>
                            <span className="badge bg-warning text-dark">
                              {fatura.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PgDashboard;
